// The storefront home page: which sections it shows, in what order, and when.
//
// Shared by the server (validation, the public view), the storefront (render
// order, and the fallback when the API is down) and the admin editor. Pure: no
// database, no DOM and no sanitizer. The server cleans custom HTML and CSS in
// ./validate.js before anything is saved.

// The sections built into the storefront code, in their default order. `type`
// is also the section's id: each one appears on the page at most once.
export const BUILT_IN_SECTIONS = [
	{ type: 'introVideo', label: 'Intro video', description: 'The “Learn more about us” bar that opens the intro video' },
	{ type: 'hero', label: 'Hero images', description: 'Five image tiles linking to the main categories' },
	{ type: 'sellingPoints', label: 'Store selling points', description: 'Three cards: customer service, free shipping, family owned' },
	{ type: 'popularProducts', label: 'Popular products', description: 'Products picked on Catalog › Products › Popular products' },
	{ type: 'featuredProducts', label: 'Featured products', description: 'Products marked Featured in BigCommerce' },
	{ type: 'popularBrands', label: 'Popular brands', description: 'Brand tiles linking to their categories' },
	{ type: 'midwife', label: 'Are you a midwife?', description: 'Image banner linking to the new birth kit setup' },
	{ type: 'missionVision', label: 'Mission, vision & values', description: 'Two rows: Our Mission and Who We Are' },
	{ type: 'about', label: 'Short about', description: 'The “In His Hands” paragraph card' },
]

// Sections an admin adds and removes.
export const CUSTOM_SECTION_TYPES = ['html', 'banner']

// The hero grid is laid out for exactly five tiles: two large, three small.
export const HERO_TILE_COUNT = 5

export const DEFAULT_HERO_TILES = [
	{ title: 'Birth Kits', description: 'Find your midwife’s custom birth kit', link: '/categories/birth-kits', image: '/images/find_your_kit_resized.jpeg' },
	{ title: 'Midwifery Equipment', description: 'Products for midwives, doulas and more', link: '/categories/midwifery-supplies', image: '/images/midwife-supplies_resized.jpeg' },
	{ title: 'Water Birth', description: 'Everything you need for a water birth', link: '/categories/water-birth', image: '/images/shop_water_birth.jpeg' },
	{ title: 'Herbal & Natural', description: 'Top quality natural products', link: '/categories/herbal-natural', image: '/images/natural-herbal-2-0129-2.jpg' },
	{ title: 'For Mothers', description: 'Pregnancy & postpartum essentials', link: '/categories/mom-baby', image: '/images/birth-supplies0129.jpg' },
]

// Category bcIds of the brand tiles, in order: Earth Mama, Birth Pool in a Box,
// Wishgarden Herbs, Masimo, Earthside Birth Pools, Earthley, Prestige Medical,
// Frida Mom. Brand pages are subcategories of the "Brands" category.
export const DEFAULT_BRAND_IDS = [180, 171, 181, 173, 182, 184, 183, 135]

export const LIMITS = {
	customSections: 30,
	brands: 24,
	name: 120,
	title: 80,
	description: 160,
	alt: 200,
	url: 2000,
	html: 100_000,
	css: 50_000,
}

const BUILT_IN_TYPES = new Set(BUILT_IN_SECTIONS.map((s) => s.type))

export const isBuiltIn = (type) => BUILT_IN_TYPES.has(type)
export const isCustom = (type) => CUSTOM_SECTION_TYPES.includes(type)

export function builtInLabel(type) {
	return BUILT_IN_SECTIONS.find((s) => s.type === type)?.label || type
}

// Custom section ids end up in a CSS attribute selector and a Vue key, so they
// are kept to a shape that needs no escaping anywhere.
export const CUSTOM_ID = /^s-[a-z0-9]{8,32}$/

export function newSectionId(random = Math.random) {
	let id = 's-'
	while (id.length < 14) id += Math.floor(random() * 36).toString(36)
	return id
}

function builtIn(raw) {
	const section = { id: raw.type, type: raw.type, enabled: raw.enabled !== false }
	if (raw.type === 'hero') {
		const tiles = Array.isArray(raw.tiles) && raw.tiles.length === HERO_TILE_COUNT ? raw.tiles : DEFAULT_HERO_TILES
		section.tiles = tiles.map((t) => ({ title: t.title || '', description: t.description || '', link: t.link || '', image: t.image || '' }))
	}
	if (raw.type === 'popularBrands') {
		const ids = Array.isArray(raw.brandIds) ? raw.brandIds.filter((id) => Number.isInteger(id) && id > 0) : []
		section.brandIds = ids.length ? ids : [...DEFAULT_BRAND_IDS]
	}
	return section
}

export function defaultSections() {
	return BUILT_IN_SECTIONS.map(builtIn)
}

// Puts a stored or submitted list into a shape every reader can trust: each
// built-in exactly once, no unknown types, and the hero always has its five
// tiles. A built-in that is missing (one added to the code after the list was
// saved) goes back in after the built-in that precedes it by default.
export function normalizeSections(list) {
	const out = []
	const seen = new Set()
	for (const raw of Array.isArray(list) ? list : []) {
		if (!raw || typeof raw !== 'object') continue
		if (isBuiltIn(raw.type)) {
			if (seen.has(raw.type)) continue
			seen.add(raw.type)
			out.push(builtIn(raw))
		} else if (isCustom(raw.type) && CUSTOM_ID.test(raw.id) && !seen.has(raw.id)) {
			seen.add(raw.id)
			out.push({ ...raw })
		}
	}
	BUILT_IN_SECTIONS.forEach(({ type }, i) => {
		if (seen.has(type)) return
		const before = BUILT_IN_SECTIONS.slice(0, i)
			.reverse()
			.find((s) => seen.has(s.type))
		const at = before ? out.findIndex((s) => s.type === before.type) + 1 : 0
		out.splice(at, 0, builtIn({ type }))
		seen.add(type)
	})
	return out
}

const toTime = (value) => {
	if (!value) return null
	const time = new Date(value).getTime()
	return Number.isFinite(time) ? time : null
}

// 'hidden' (switched off), 'upcoming' (scheduled, not started), 'ended'
// (scheduled, past its end) or 'live'. Built-ins are never scheduled.
export function sectionState(section, now = new Date()) {
	if (!section || section.enabled === false) return 'hidden'
	if (!section.scheduled) return 'live'
	const t = now.getTime()
	const start = toTime(section.startsAt)
	const end = toTime(section.endsAt)
	if (start !== null && t < start) return 'upcoming'
	if (end !== null && t >= end) return 'ended'
	return 'live'
}

export const isSectionLive = (section, now = new Date()) => sectionState(section, now) === 'live'

// Link and image addresses: site paths ("/categories/water-birth") and https
// URLs. Returns '' for an empty value and null for one that is not allowed.
export function cleanUrl(value) {
	const s = typeof value === 'string' ? value.trim() : ''
	if (!s) return ''
	if (s.length > LIMITS.url || /[\s<>"'`\\]/.test(s)) return null
	if (s.startsWith('/')) return s.startsWith('//') ? null : s
	try {
		const url = new URL(s)
		return url.protocol === 'https:' ? url.href : null
	} catch {
		return null
	}
}

export const POPULAR_PRODUCT_FIELDS = ['id', 'name', 'customUrl', 'images', 'alt', 'rating', 'bcId', 'sku', 'popular', 'popularOrder']
export const BRAND_FIELDS = ['name', 'url', 'imageUrl', 'bcId']

// The data a product or brand row loads, as a Mango collection query, or null
// for sections that load nothing. The row components run these in the browser,
// and the storefront server (front/public/index.js) runs the same ones so the
// home page arrives with its rows already filled.
export function rowQuery(section) {
	if (section?.type === 'popularProducts') return { collection: 'products', query: { search: { popular: true }, limit: 20, fields: POPULAR_PRODUCT_FIELDS } }
	if (section?.type === 'featuredProducts') return { collection: 'products', query: { search: { isFeatured: true }, limit: 20, sort: { sortOrder: 1 } } }
	if (section?.type === 'popularBrands') {
		const ids = section.brandIds?.length ? section.brandIds : DEFAULT_BRAND_IDS
		return { collection: 'categories', query: { search: { bcId: { $in: ids } }, fields: BRAND_FIELDS, limit: 50 } }
	}
	return null
}

// A Mango REST query string, built the way the storefront's Mango helper builds it.
export function mangoQueryString({ limit, page, search, fields, sort } = {}) {
	const params = { limit, page, search, fields, sort }
	for (const key of ['search', 'fields', 'sort']) if (params[key] !== undefined) params[key] = JSON.stringify(params[key])
	return Object.keys(params)
		.filter((key) => params[key] !== undefined)
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
		.join('&')
}

// Brand categories in the order they were picked; ids with no category are skipped.
export function orderBrands(categories, ids = DEFAULT_BRAND_IDS) {
	const list = Array.isArray(categories) ? categories : []
	return ids.map((id) => list.find((c) => c?.bcId === id)).filter(Boolean)
}

// Site paths ("/images/…") live on the storefront. The admin UI runs on another
// host, so it needs the full address to show them.
export function storefrontUrl(value, origin = 'https://inhishands.com') {
	if (typeof value !== 'string' || !value) return ''
	return value.startsWith('/') ? `${origin}${value}` : value
}
